'use client'

import {
  Children,
  type PropsWithChildren,
  useEffect,
  useId,
  useState,
} from 'react'
import { Checkbox as CheckboxUI, Field } from '@headlessui/react'

import { cn } from '@mntn-dev/ui-utilities'

import { forwardRef } from '../../utils/forward-ref.ts'
import {
  type UseFormFieldControlProps,
  useFormFieldControl,
} from '../form-field/form-field.tsx'
import { Icon } from '../icon/icon.tsx'

type CheckboxProps = Readonly<
  PropsWithChildren<
    {
      value?: boolean
      onChange?: (value: boolean) => void
      className?: string
      /** Prevents the label click from bubbling so parent click handlers only see the control’s click */
      stopLabelPropagation?: boolean
    } & UseFormFieldControlProps<HTMLButtonElement>
  >
>

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      value,
      onChange,
      disabled: disabledProp,
      id,
      hasError: hasErrorProp,
      hasWarning: hasWarningProp,
      hasSuccess: hasSuccessProp,
      readOnly: readOnlyProp,
      stopLabelPropagation,
      children,
      dataTestId,
      dataTrackingId,
      ...checkboxProps
    },
    ref
  ) => {
    const {
      id: controlId,
      disabled,
      hasError,
      hasSuccess,
      hasWarning,
      readOnly,
      ...fieldProps
    } = useFormFieldControl<HTMLButtonElement>({
      disabled: disabledProp,
      id,
      hasError: hasErrorProp,
      hasWarning: hasWarningProp,
      hasSuccess: hasSuccessProp,
      readOnly: readOnlyProp,
      dataTestId,
      dataTrackingId,
    })

    const generatedId = useId()
    // when there are children to be rendered as a label, use id so multiple checkboxes in one FormField stay unique
    // when there are no children, use controlId so external FormField.Label matches
    const hasOwnLabel = Children.count(children) > 0
    const resolvedId = (hasOwnLabel ? id : (controlId ?? id)) ?? generatedId

    const [isChecked, setChecked] = useState<boolean | undefined>(value)
    const isIndeterminate = isChecked === undefined
    const isActive = isChecked || isIndeterminate

    useEffect(() => {
      setChecked(value)
    }, [value])

    const handleChange = (value: boolean) => {
      if (readOnly) {
        return
      }

      setChecked(value)

      if (onChange) {
        onChange(value)
      }
    }

    return (
      <Field className="flex gap-x-3 items-center">
        <CheckboxUI
          as="button"
          type="button"
          ref={ref}
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleChange}
          disabled={disabled}
          {...checkboxProps}
          {...fieldProps}
          id={resolvedId}
        >
          <span
            className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-xs border-[0.5px] p-1',
              hasError
                ? 'bgg-glass-well border-negative'
                : isActive
                  ? 'bgg-glass-well-highlight border-brand'
                  : 'bgg-glass-well border-white/20',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {isChecked && (
              <Icon
                fill="solid"
                name="check"
                size="xs"
                className={cn('fill-brand', hasError && 'fill-negative')}
              />
            )}

            {isIndeterminate && (
              <Icon
                fill="solid"
                name="minus"
                size="xs"
                className={cn('fill-brand', hasError && 'fill-negative')}
              />
            )}
          </span>
        </CheckboxUI>

        <label
          htmlFor={resolvedId}
          className={cn('cursor-pointer', hasError && '*:text-primary-red')}
          onClick={
            stopLabelPropagation ? (e) => e.stopPropagation() : undefined
          }
        >
          {children}
        </label>
      </Field>
    )
  }
)

export { Checkbox, type CheckboxProps }
